/**
 * FS-09 리모컨 입력 — 순수 로직(DOM 없음). SR-RUN-04 · SCR v0.6 FS-09-01/02.
 *
 *  - 상태: {dpad(8방향|null), a, b, slider(-100~100)} = protocol RemoteInput 그대로.
 *  - 출처: 터치(포인터) · 키보드 · 게임패드. 출처마다 따로 모으고 {@link mergeSources} 로 합친다.
 *  - 전송: 바뀔 때만, 최대 maxHz(기본 30) — {@link RemoteSender}. 넘치는 변화는 합쳐서(마지막 값) 다음 칸에 보낸다.
 *    합쳐진(버려진) 중간 값 수는 FE-306 계수로 남긴다(학생에게는 보이지 않음 — FE_META.studentSilent).
 *  - SE(FS-09-02): 4방향 · 슬라이더 대신 2단 속도(천천히 50 / 빨리 100 — 계약 C-05 · 결정 2026-09-25).
 *
 * 허브 매핑(커널 쪽 — 여기서는 힌트 문구에만 쓴다): A → 허브 LEFT, B → 허브 RIGHT(버튼만),
 * 슬라이더 → liveValue, 북/남 + 슬라이더 0 → ±100. 서/동 방향은 허브 버튼을 누르지 않는다(프로그램의 onDpad 만).
 */
import type { Dpad, RemoteInput } from "@picnic-field/local-host/protocol";
import type {
  RemoteVariant,
  KeyAction,
  PadSnapshot,
  SourceState,
  Clock,
  SenderStats,
} from "../types";

export const NEUTRAL: RemoteInput = Object.freeze({ dpad: null, a: false, b: false, slider: 0 });

/** SE 2단 속도(슬라이더 대신) — 계약 값. 와이어 FS-09-02 각주(±60/±100)와 다르다: 보고서 참고 */
export const SE_SPEED = { slow: 50, fast: 100 } as const;
export type SeSpeed = keyof typeof SE_SPEED;

/** SU 슬라이더 기본값(와이어 FS-09-01 · FS-04-SU '속도 +70') */
export const SU_SLIDER_DEFAULT = 70;
/** Q/E · 게임패드 LB/RB 한 번에 움직이는 양 */
export const SLIDER_STEP = 10;

export const clampSlider = (v: number) =>
  Math.max(-100, Math.min(100, Math.round(Number.isFinite(v) ? v : 0)));

export function sameInput(x: RemoteInput, y: RemoteInput): boolean {
  return x.dpad === y.dpad && x.a === y.a && x.b === y.b && x.slider === y.slider;
}

// ── 방향 ───────────────────────────────────────────────────────────────

/** 축 벡터(x: 동+, y: 북+) → 8방향. 0,0 → null */
export function dpadFromAxes(x: number, y: number): Dpad {
  const sx = Math.sign(x);
  const sy = Math.sign(y);
  if (!sx && !sy) return null;
  const ns = sy > 0 ? "n" : sy < 0 ? "s" : "";
  const ew = sx > 0 ? "e" : sx < 0 ? "w" : "";
  return (ns + ew) as Dpad;
}

/** 8방향 → 축 벡터 */
export function axesOf(d: Dpad): [number, number] {
  if (!d) return [0, 0];
  const x = d.includes("e") ? 1 : d.includes("w") ? -1 : 0;
  const y = d.includes("n") ? 1 : d.includes("s") ? -1 : 0;
  return [x, y];
}

/**
 * SE 4방향 — 대각선은 마지막에 누른 축 하나만 남긴다(lastAxis). 저학년이 두 버튼을 함께 눌러도
 * 예측 가능한 한 방향으로만 간다.
 */
export function toFourWay(d: Dpad, lastAxis: "x" | "y" = "y"): Dpad {
  if (!d || d.length === 1) return d;
  const [x, y] = axesOf(d);
  return lastAxis === "x" ? dpadFromAxes(x, 0) : dpadFromAxes(0, y);
}

// ── 키보드 ─────────────────────────────────────────────────────────────

/**
 * 키 매핑(SCR FS-09-01 키 힌트): 방향키 / WASD · Q/E 속도 · R 처음부터 · Esc 그만.
 * A/B 버튼은 Z/X(와이어 힌트에는 없음 — 버튼에 aria-keyshortcuts 로 알린다). WASD 의 A 는 방향이다.
 * `code` 기준이라 한글 입력 상태에서도 같은 키다.
 */
export function keyAction(code: string): KeyAction | null {
  switch (code) {
    case "ArrowUp":
    case "KeyW":
      return { kind: "dir", axis: "y", sign: 1 };
    case "ArrowDown":
    case "KeyS":
      return { kind: "dir", axis: "y", sign: -1 };
    case "ArrowLeft":
    case "KeyA":
      return { kind: "dir", axis: "x", sign: -1 };
    case "ArrowRight":
    case "KeyD":
      return { kind: "dir", axis: "x", sign: 1 };
    case "KeyZ":
      return { kind: "button", button: "a" };
    case "KeyX":
      return { kind: "button", button: "b" };
    case "KeyQ":
      return { kind: "slider", delta: -1 };
    case "KeyE":
      return { kind: "slider", delta: 1 };
    case "KeyR":
      return { kind: "reset" };
    case "Escape":
      return { kind: "stop" };
    default:
      return null;
  }
}

/** 눌린 키 집합 → 방향·버튼. 반대 방향을 함께 누르면 그 축은 0 */
export function fromPressedKeys(codes: Iterable<string>): { dpad: Dpad; a: boolean; b: boolean } {
  let x = 0;
  let y = 0;
  let a = false;
  let b = false;
  const seen = new Set<string>();
  for (const c of codes) {
    const act = keyAction(c);
    if (!act) continue;
    if (act.kind === "dir") {
      const k = `${act.axis}${act.sign}`;
      if (seen.has(k)) continue; // 방향키와 WASD 를 같이 눌러도 한 번
      seen.add(k);
      if (act.axis === "x") x += act.sign;
      else y += act.sign;
    } else if (act.kind === "button") {
      if (act.button === "a") a = true;
      else b = true;
    }
  }
  return { dpad: dpadFromAxes(x, y), a, b };
}

// ── 게임패드(Standard Gamepad 배치) ───────────────────────────────────────

export const PAD_DEADZONE = 0.35;
/** Standard mapping 버튼 번호 */
export const PAD = {
  A: 0,
  B: 1,
  LB: 4,
  RB: 5,
  BACK: 8,
  START: 9,
  UP: 12,
  DOWN: 13,
  LEFT: 14,
  RIGHT: 15,
} as const;

const pressed = (p: PadSnapshot, i: number) => p.buttons[i]?.pressed === true;

/** 왼쪽 스틱(데드존) 또는 방향 버튼 → 8방향 · A/B(0/1) */
export function fromGamepad(p: PadSnapshot): { dpad: Dpad; a: boolean; b: boolean } {
  const ax = p.axes[0] ?? 0;
  const ay = p.axes[1] ?? 0;
  let x = Math.abs(ax) >= PAD_DEADZONE ? Math.sign(ax) : 0;
  // 스틱 Y 는 아래가 + — 북이 + 가 되게 뒤집는다
  let y = Math.abs(ay) >= PAD_DEADZONE ? -Math.sign(ay) : 0;
  if (pressed(p, PAD.LEFT)) x = -1;
  if (pressed(p, PAD.RIGHT)) x = 1;
  if (pressed(p, PAD.UP)) y = 1;
  if (pressed(p, PAD.DOWN)) y = -1;
  return { dpad: dpadFromAxes(x, y), a: pressed(p, PAD.A), b: pressed(p, PAD.B) };
}

/** 가장자리(눌린 순간) 검출 — LB/RB 속도 · BACK 처음부터 · START 그만 */
export function padEdges(prev: PadSnapshot | null, cur: PadSnapshot) {
  const edge = (i: number) => pressed(cur, i) && !(prev && pressed(prev, i));
  return {
    sliderDown: edge(PAD.LB),
    sliderUp: edge(PAD.RB),
    reset: edge(PAD.BACK),
    stop: edge(PAD.START),
  };
}

// ── 출처 합치기 ─────────────────────────────────────────────────────────

/**
 * 터치·키보드·게임패드를 합친다. 방향은 우선순위(터치 > 키보드 > 게임패드) 중 처음으로 방향이 있는 것,
 * 버튼은 OR. SE 는 4방향으로 접는다.
 */
export function mergeSources(
  sources: readonly SourceState[],
  slider: number,
  variant: RemoteVariant,
  lastAxis: "x" | "y" = "y",
): RemoteInput {
  let dpad: Dpad = null;
  let a = false;
  let b = false;
  for (const s of sources) {
    if (!dpad && s.dpad) dpad = s.dpad;
    a ||= s.a;
    b ||= s.b;
  }
  if (variant === "se") dpad = toFourWay(dpad, lastAxis);
  return { dpad, a, b, slider: clampSlider(slider) };
}

// ── 전송(속도 제한) ─────────────────────────────────────────────────────

export const realClock: Clock = {
  now: () => performance.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

/**
 * 바뀔 때만 · 최대 maxHz 로 보낸다. 칸(1000/maxHz ms) 안에서 여러 번 바뀌면 마지막 값만 칸이 열릴 때 보낸다
 * (앞쪽 가장자리는 즉시 — 첫 입력의 지연을 늘리지 않는다).
 */
export class RemoteSender {
  private last: RemoteInput | null = null;
  private lastAt = -Infinity;
  private pending: RemoteInput | null = null;
  private timer: unknown = null;
  readonly stats: SenderStats = { sent: 0, unchanged: 0, coalesced: 0 };

  constructor(
    private readonly send: (s: RemoteInput) => void,
    private maxHz = 30,
    private readonly clock: Clock = realClock,
  ) {}

  get intervalMs(): number {
    return 1000 / Math.max(1, this.maxHz);
  }

  setMaxHz(hz: number) {
    this.maxHz = hz;
  }

  /** 마지막으로 보낸 값(없으면 null) */
  get lastSent(): RemoteInput | null {
    return this.last;
  }

  push(s: RemoteInput) {
    const target = this.pending ?? this.last;
    if (target && sameInput(target, s)) {
      this.stats.unchanged += 1;
      return;
    }
    if (this.pending) {
      // 칸이 열리기 전에 또 바뀜 — 앞의 대기 값은 버린다
      this.stats.coalesced += 1;
      this.pending = s;
      if (this.last && sameInput(this.last, s)) {
        // 되돌아왔으면 보낼 필요 없음
        this.pending = null;
        this.clock.clearTimeout(this.timer);
        this.timer = null;
      }
      return;
    }
    const wait = this.lastAt + this.intervalMs - this.clock.now();
    if (wait <= 0) {
      this.flush(s);
      return;
    }
    this.pending = s;
    this.timer = this.clock.setTimeout(() => {
      this.timer = null;
      const p = this.pending;
      this.pending = null;
      if (p) this.flush(p);
    }, wait);
  }

  private flush(s: RemoteInput) {
    this.last = s;
    this.lastAt = this.clock.now();
    this.stats.sent += 1;
    this.send(s);
  }

  /** 대기 중인 값을 버리고 멈춘다(화면 이탈) */
  dispose() {
    if (this.timer != null) this.clock.clearTimeout(this.timer);
    this.timer = null;
    this.pending = null;
  }

  /** 다음 판을 위해 '마지막으로 보낸 값'을 잊는다(새 호스트는 중립에서 시작) */
  resetBaseline(s: RemoteInput | null = null) {
    this.dispose();
    this.last = s;
    this.lastAt = -Infinity;
  }
}

// ── 지연 계측(입력 → 반영된 프레임) ──────────────────────────────────────

/**
 * 입력을 보낸 시각 → 그 뒤 처음 받은 커널 프레임(= 입력이 반영되는 첫 틱) 까지의 지연.
 * 렌더(다음 페인트)까지는 `markPainted` 로 따로 잰다. p95 는 최근 N개 표본에서.
 */
export class LatencyProbe {
  private waiting: number | null = null;
  private waitingPaint: number | null = null;
  readonly frameMs: number[] = [];
  readonly paintMs: number[] = [];

  constructor(private readonly cap = 256) {}

  markInput(t: number) {
    if (this.waiting == null) this.waiting = t;
  }

  /** 커널 프레임을 받음 */
  markFrame(t: number) {
    if (this.waiting == null) return;
    push(this.frameMs, t - this.waiting, this.cap);
    this.waitingPaint = this.waiting;
    this.waiting = null;
  }

  /** 그 프레임이 화면에 그려짐 */
  markPainted(t: number) {
    if (this.waitingPaint == null) return;
    push(this.paintMs, t - this.waitingPaint, this.cap);
    this.waitingPaint = null;
  }

  summary() {
    return { frame: summarize(this.frameMs), paint: summarize(this.paintMs) };
  }
}

function push(xs: number[], v: number, cap: number) {
  xs.push(v);
  if (xs.length > cap) xs.shift();
}

export function percentile(xs: readonly number[], p: number): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const i = Math.min(s.length - 1, Math.max(0, Math.ceil((p / 100) * s.length) - 1));
  return s[i] ?? null;
}

function summarize(xs: readonly number[]) {
  return { n: xs.length, p50: percentile(xs, 50), p95: percentile(xs, 95) };
}
